// Helpers purs de la section « Expériences » (A/B testing).
//
// Tout ce qui se teste sans navigateur vit ici : validation du formulaire,
// traduction « leviers du formulaire » ⇄ « config de variante » envoyée à
// l'API, et libellés français des statuts/verdicts. Les composants React ne
// gardent que le rendu et les appels réseau.
//
// Contrat de config (partagé avec le back et le front, cf. plan phase 2) :
//   surface "home"    → { blocks: [clés], categories: [urls] }
//   surface "product" → { blocksOrder: [clés], cta: {label, color},
//                         voucherInCta: bool, highlightMerchant: string,
//                         sizesFirst: bool, bestOfferFirst: bool }
//   toute variante peut porter `variantKey` (variante CODÉE côté front).
//
// Règle de fusion côté serveur : « défaut de surface ⊕ config de variante »,
// clé par clé. Une clé ABSENTE de la config hérite donc du défaut de la
// surface. C'est pourquoi les booléens ne sont écrits que lorsqu'ils sont
// vrais : un `false` explicite écraserait un défaut de surface à `true`.

export const EXPERIMENT_SURFACES = [
    { value: "home", label: "Page d'accueil" },
    { value: "product", label: "Page produit" },
    { value: "site", label: "Site entier — nouveau design (v2)" },
];

// Métrique primaire imposée par la surface (le back refuse toute autre valeur).
export const METRIC_BY_SURFACE = {
    home: "home_click_rate",
    product: "outbound_click_rate",
    site: "outbound_click_rate",
};

// Métrique de contrôle : affichée, jamais décisionnelle.
export const CONTROL_METRIC = "commission_per_visitor";

// Vocabulaire des résultats par métrique principale : une « conversion » du
// moteur est un clic sortant marchand (redirection) ou une vue de fiche produit,
// jamais une vente — les ventes sont dans le bloc de contrôle (commission).
export const METRIC_TERMS = {
    outbound_click_rate: { count: "Redirections", rate: "Taux de redirection" },
    home_click_rate: { count: "Clics vers une fiche produit", rate: "Taux de clic fiche produit" },
};
export const metricTerms = (experiment) =>
    METRIC_TERMS[experiment && experiment.metric && experiment.metric.primary] || METRIC_TERMS.outbound_click_rate;

export const METRIC_LABELS = {
    home_click_rate: "Taux de clic vers une fiche produit (jour de l'exposition)",
    outbound_click_rate: "Taux de clic sortant marchand (7 jours)",
    commission_per_visitor: "Commission par visiteur exposé",
};

// Ordre par défaut des blocs de la home (branche mobile du front —
// helpers/experiments/variants/home-blocks-order.js). Sert d'inventaire au
// sélecteur : l'ordre choisi par l'utilisateur est celui de sa sélection.
export const HOME_BLOCK_KEYS = [
    "search",
    "slides",
    "categories",
    "adFirst",
    "mostWanted",
    "physicalShops",
    "vouchers",
    "infiniteProducts",
    "merchants",
    "guides",
    "privateSales",
    "adThird",
    "races",
    "editorial",
];

export const HOME_BLOCK_LABELS = {
    search: "Recherche",
    slides: "Slider",
    categories: "Catégories",
    adFirst: "Publicité (haut)",
    mostWanted: "Les plus recherchés",
    physicalShops: "Magasins physiques",
    vouchers: "Codes promo",
    infiniteProducts: "Mise en avant infinie",
    merchants: "Marchands",
    guides: "Guides",
    privateSales: "Ventes privées",
    adThird: "Publicité (bas)",
    races: "Courses",
    editorial: "Éditorial",
};

// Ordre par défaut des blocs de la page produit (branche mobile).
export const PRODUCT_BLOCK_KEYS = [
    "headerSticky",
    "sizeChooser",
    "bestOffer",
    "imageDescription",
    "merchantOffers",
    "history",
    "editorial",
    "articles",
    "ratings",
    "addRating",
    "toKnow",
    "relatedApparel",
    "similarProducts",
];

export const PRODUCT_BLOCK_LABELS = {
    headerSticky: "En-tête collant",
    sizeChooser: "Choix de la taille",
    bestOffer: "Meilleure offre",
    imageDescription: "Images et description",
    merchantOffers: "Offres marchands",
    history: "Historique des prix",
    editorial: "Texte éditorial",
    articles: "Articles liés",
    ratings: "Avis",
    addRating: "Déposer un avis",
    toKnow: "À savoir",
    relatedApparel: "Textile associé",
    similarProducts: "Produits similaires",
};

export const STATUS_LABELS = {
    draft: "Brouillon",
    running: "En cours",
    paused: "En pause",
    concluded: "Conclue",
};

export const STATUS_COLORS = {
    draft: "secondary",
    running: "success",
    paused: "warning",
    concluded: "primary",
};

export const VERDICT_LABELS = {
    continue: "Lecture intermédiaire",
    stop_winner: "Gagnant proposé",
    stop_no_diff: "Aucune différence pratique",
    stop_max_days: "Durée maximale atteinte, sans conclusion",
};

// Garde-fous par défaut (miroir de model.DefaultRules côté Go, plus le seuil de
// perte en revenu ajouté en phase 2).
export const DEFAULT_RULES = {
    minDays: 7,
    minExposures: 1000,
    maxDays: 28,
    expectedLossThreshold: 0.001,
    expectedLossThresholdRevenue: 0.05,
};

export function surfaceLabel(surface) {
    const found = EXPERIMENT_SURFACES.find((s) => s.value === surface);
    return found ? found.label : surface || "—";
}

export function statusLabel(status) {
    return STATUS_LABELS[status] || status || "—";
}

export function statusColor(status) {
    return STATUS_COLORS[status] || "secondary";
}

export function verdictLabel(decision) {
    return VERDICT_LABELS[decision] || decision || "—";
}

export function blockKeysForSurface(surface) {
    return surface === "product" ? PRODUCT_BLOCK_KEYS : HOME_BLOCK_KEYS;
}

export function blockLabel(surface, key) {
    const labels = surface === "product" ? PRODUCT_BLOCK_LABELS : HOME_BLOCK_LABELS;
    return labels[key] || key;
}

// Squelette d'une expérience neuve : contrôle A sans levier + variante B
// porteuse des leviers, 50/50, garde-fous par défaut.
export function emptyExperiment(surface = "home") {
    return {
        id: "",
        name: "",
        hypothesis: "",
        surface,
        status: "draft",
        variants: [
            { key: "A", name: "Contrôle", weight: 50, config: {} },
            { key: "B", name: "Variante", weight: 50, config: {} },
        ],
        targeting: { markets: [], sources: [], devices: [], loggedIn: "any" },
        metric: { primary: METRIC_BY_SURFACE[surface], control: CONTROL_METRIC },
        rules: { ...DEFAULT_RULES },
        winner: "",
    };
}

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";
const toNumber = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));

// validateExperimentForm : liste des erreurs bloquantes, en français, dans
// l'ordre du formulaire. Tableau vide ⇒ le formulaire peut être envoyé.
export function validateExperimentForm(form) {
    const errors = [];
    const f = form || {};

    if (isBlank(f.name)) errors.push("Le nom de l'expérience est obligatoire.");
    if (isBlank(f.hypothesis)) {
        errors.push("L'hypothèse est obligatoire : écrire ce que l'on attend et pourquoi.");
    }
    if (!METRIC_BY_SURFACE[f.surface]) {
        errors.push("La surface doit être la page d'accueil ou la page produit.");
    }

    const variants = Array.isArray(f.variants) ? f.variants : [];
    if (variants.length < 2) {
        errors.push("Une expérience a besoin d'au moins 2 variantes.");
    } else {
        if (variants[0].key !== "A") {
            errors.push("La première variante doit être le contrôle « A ».");
        }
        const seen = {};
        let total = 0;
        variants.forEach((variant, index) => {
            if (isBlank(variant.key)) errors.push("La clé de la variante n° " + (index + 1) + " est vide.");
            else if (seen[variant.key]) errors.push("La clé de variante « " + variant.key + " » est en double.");
            else seen[variant.key] = true;

            if (isBlank(variant.name)) {
                errors.push("La variante « " + (variant.key || index + 1) + " » doit avoir un nom.");
            }
            const weight = toNumber(variant.weight);
            if (!Number.isFinite(weight) || weight < 0 || !Number.isInteger(weight)) {
                errors.push("Le poids de la variante « " + (variant.key || index + 1) + " » doit être un entier positif.");
            } else {
                total += weight;
            }
        });
        if (total !== 100) {
            errors.push("La somme des poids doit faire 100 % (actuellement " + total + " %).");
        }
    }

    const rules = f.rules || {};
    const minDays = toNumber(rules.minDays);
    const maxDays = toNumber(rules.maxDays);
    const minExposures = toNumber(rules.minExposures);
    const threshold = toNumber(rules.expectedLossThreshold);
    const thresholdRevenue = toNumber(rules.expectedLossThresholdRevenue);

    if (!Number.isFinite(minDays) || minDays < 1) errors.push("La durée minimale doit être d'au moins 1 jour.");
    if (Number.isFinite(maxDays) && maxDays > 0 && Number.isFinite(minDays) && maxDays < minDays) {
        errors.push("La durée maximale doit être supérieure à la durée minimale.");
    }
    if (!Number.isFinite(minExposures) || minExposures < 0) {
        errors.push("Le nombre minimal d'expositions ne peut pas être négatif.");
    }
    if (!Number.isFinite(threshold) || threshold < 0) {
        errors.push("Le seuil de perte attendue ne peut pas être négatif.");
    }
    if (!Number.isFinite(thresholdRevenue) || thresholdRevenue < 0) {
        errors.push("Le seuil de perte attendue en revenu ne peut pas être négatif.");
    }

    return errors;
}

const cleanList = (list) =>
    (Array.isArray(list) ? list : [])
        .map((v) => String(v || "").trim())
        .filter((v) => v !== "");

// leversToConfig : leviers du formulaire → objet `config` de la variante.
// Les leviers vides sont OMIS (une clé absente hérite du défaut de la surface).
export function leversToConfig(surface, levers) {
    const l = levers || {};
    const config = {};

    if (!isBlank(l.variantKey)) config.variantKey = String(l.variantKey).trim();

    if (surface === "home") {
        const blocks = cleanList(l.blocks);
        if (blocks.length > 0) config.blocks = blocks;
        const categories = cleanList(l.categories);
        if (categories.length > 0) config.categories = categories;
        const searchSuggestions = parseSuggestions(l.searchSuggestions);
        if (searchSuggestions.length > 0) config.searchSuggestions = searchSuggestions;
        return config;
    }

    if (surface === "product") {
        const blocksOrder = cleanList(l.blocksOrder);
        if (blocksOrder.length > 0) config.blocksOrder = blocksOrder;

        const cta = {};
        if (!isBlank(l.ctaLabel)) cta.label = String(l.ctaLabel).trim();
        if (!isBlank(l.ctaColor)) cta.color = String(l.ctaColor).trim();
        if (Object.keys(cta).length > 0) config.cta = cta;

        if (l.voucherInCta === true) config.voucherInCta = true;
        if (!isBlank(l.highlightMerchant)) config.highlightMerchant = String(l.highlightMerchant).trim();
        if (l.sizesFirst === true) config.sizesFirst = true;
        if (l.bestOfferFirst === true) config.bestOfferFirst = true;
        return config;
    }

    return config;
}

// configToLevers : objet `config` d'une variante → leviers du formulaire.
// Réciproque de leversToConfig (aller-retour stable, cf. tests).
export function configToLevers(surface, config) {
    const c = config && typeof config === "object" ? config : {};
    const base = { variantKey: typeof c.variantKey === "string" ? c.variantKey : "" };

    if (surface === "home") {
        return {
            ...base,
            blocks: cleanList(c.blocks),
            categories: cleanList(c.categories),
            // Clé présente seulement si la config en a : l'aller-retour des variantes
            // sans suggestions reste identique à avant.
            ...(Array.isArray(c.searchSuggestions) && c.searchSuggestions.length > 0
                ? { searchSuggestions: formatSuggestions(c.searchSuggestions) }
                : {}),
        };
    }

    if (surface === "product") {
        const cta = c.cta && typeof c.cta === "object" ? c.cta : {};
        return {
            ...base,
            blocksOrder: cleanList(c.blocksOrder),
            ctaLabel: typeof cta.label === "string" ? cta.label : "",
            ctaColor: typeof cta.color === "string" ? cta.color : "",
            voucherInCta: c.voucherInCta === true,
            highlightMerchant: typeof c.highlightMerchant === "string" ? c.highlightMerchant : "",
            sizesFirst: c.sizesFirst === true,
            bestOfferFirst: c.bestOfferFirst === true,
        };
    }

    return base;
}

// isCodedVariant : variante CODÉE côté front (elle porte un `variantKey`).
// Conclure sur une telle variante ne peut pas être appliqué en données : elle
// reste servie à 100 % par le middleware, sans cache CDN, jusqu'au
// déploiement du front. À l'inverse, une variante « en données » voit sa
// config recopiée dans les défauts de la surface.
export function isCodedVariant(variant) {
    const config = variant && variant.config;
    return !!(config && typeof config.variantKey === "string" && config.variantKey.trim() !== "");
}

// hasAnyConfig : la variante porte-t-elle une config non vide ? Miroir exact du
// `len(winnerVariant.Config) == 0` du backend — le nombre de clés, sans juger
// de leur contenu.
function hasAnyConfig(variant) {
    const config = variant && variant.config;
    return !!(config && typeof config === "object" && Object.keys(config).length > 0);
}

// concludeOutcome : ce que la conclusion va RÉELLEMENT faire, selon la variante
// gagnante (sémantique backend, phase 2) :
//   "control" → le contrôle A, ou une variante sans config : rien n'est copié,
//               l'expérience s'arrête et le site revient aux défauts de surface ;
//   "data"    → variante en données : sa config est copiée dans les défauts ;
//   "coded"   → variante codée (`variantKey`) : servie à 100 % sans cache CDN
//               jusqu'au déploiement du front (rien n'est copié non plus).
//
// L'ORDRE des tests reprend celui de `applyDataWinner` côté backend : le
// contrôle est examiné AVANT la variante codée — conclure sur A veut dire « on
// garde le rendu actuel », qu'il porte ou non un `variantKey`.
export function concludeOutcome(variant) {
    if (!variant || variant.key === "A") return "control";
    if (!hasAnyConfig(variant)) return "control";
    if (isCodedVariant(variant)) return "coded";
    return "data";
}

export const CONCLUDE_WARNINGS = {
    control: "Contrôle conservé : aucune config copiée, l'expérience s'arrête et le site revient aux défauts de la surface.",
    data: "Variante en données : sa config sera copiée dans les défauts de la surface, l'expérience s'arrête.",
    coded: "Variante codée (variantKey) : servie à 100 % sans cache CDN jusqu'au déploiement du front.",
};

// concludeWarning : avertissement affiché dans la modale de conclusion, selon
// la nature de la variante gagnante (trois cas, cf. concludeOutcome).
export function concludeWarning(variant) {
    const outcome = concludeOutcome(variant);
    if (outcome === "coded") {
        return (
            "Variante codée (" +
            variant.config.variantKey +
            ") : servie à 100 % sans cache CDN jusqu'au déploiement du front."
        );
    }
    return CONCLUDE_WARNINGS[outcome];
}

// concludeWarningTone : couleur d'alerte bootstrap associée à l'avertissement.
export function concludeWarningTone(variant) {
    const outcome = concludeOutcome(variant);
    if (outcome === "coded") return "warning";
    if (outcome === "data") return "info";
    return "secondary";
}

// applyLeversToVariants : pose la config issue des leviers sur la SEULE variante
// éditée, et laisse les autres variantes intactes. Le formulaire n'édite que
// A/B, mais le modèle Go accepte davantage de variantes : écraser les configs
// des variantes non affichées effacerait silencieusement un test à trois bras.
export function applyLeversToVariants(variants, editedKey, config) {
    return (Array.isArray(variants) ? variants : []).map((variant) =>
        variant.key === editedKey
            ? { ...variant, config }
            : { ...variant, config: variant.config && typeof variant.config === "object" ? variant.config : {} }
    );
}

// --- Catégories de la home -------------------------------------------------
//
// `GET /appearances?headCategory=/appearance/home/search-category-V2` renvoie
// TOUTES les vignettes de catégorie, tous univers (`shopTypes`) et toutes
// langues confondus. La home testée est celle de l'athlétisme/running, dans la
// langue du marché ciblé : proposer les autres produirait une config que le
// front n'appliquerait jamais.

export const CATEGORY_SHOP_TYPE = "athletisme";

const SUPPORTED_LANGUAGES = ["fr", "es"];

// marketLanguage : langue de contenu d'un marché (fr → fr, es → es). Le marché
// ciblé de l'expérience est sa PREMIÈRE cible de marché ; aucun ciblage = fr.
export function marketLanguage(market) {
    const value = String(market || "").trim().toLowerCase();
    return SUPPORTED_LANGUAGES.includes(value) ? value : "fr";
}

export function experimentMarket(form) {
    const markets = (form && form.targeting && form.targeting.markets) || [];
    return marketLanguage(Array.isArray(markets) && markets.length > 0 ? markets[0] : "fr");
}

// appearanceShopTypes : univers d'une vignette. ⚠️ Le transport du back_api
// sérialise le champ en `shopTypeNG` (et non `shopTypes`, qui est le nom du
// champ Go) — c'est ce nom-là que renvoie `GET /appearances` et qu'utilise tout
// le reste de pikachu. `shopTypes` est accepté en repli au cas où le contrat
// changerait.
function appearanceShopTypes(appearance) {
    const a = appearance || {};
    const raw = Array.isArray(a.shopTypeNG) ? a.shopTypeNG : Array.isArray(a.shopTypes) ? a.shopTypes : [];
    return raw.map((s) => String(s || "").trim().toLowerCase());
}

// filterCategoryAppearances : garde les vignettes de l'univers athlétisme dans
// la langue du marché. Une langue absente vaut « fr » (données historiques
// créées avant l'ouverture du marché espagnol).
export function filterCategoryAppearances(list, market) {
    const language = marketLanguage(market);
    return (Array.isArray(list) ? list : []).filter((appearance) => {
        if (!appearance || !appearance.url) return false;
        if (!appearanceShopTypes(appearance).includes(CATEGORY_SHOP_TYPE)) return false;
        const appearanceLanguage = String(appearance.language || "fr").trim().toLowerCase() || "fr";
        return appearanceLanguage === language;
    });
}

// categoryAppearanceOption : option react-select d'une vignette de catégorie.
// Le libellé rappelle l'univers et la langue — deux vignettes peuvent porter le
// même titre d'un marché à l'autre.
export function categoryAppearanceOption(appearance) {
    const a = appearance || {};
    const shopTypes = appearanceShopTypes(a).join(", ");
    const language = String(a.language || "fr").trim().toLowerCase() || "fr";
    const context = [shopTypes, language].filter((v) => v !== "").join(" · ");
    return {
        value: a.url,
        label: (a.title || a.url) + (context ? " [" + context + "]" : "") + " — " + a.url,
    };
}

// apiErrorMessage : message lisible à partir d'une réponse d'erreur de l'API.
// Le back renvoie `{"message":"…"}` — c'est ce texte qu'il faut montrer, pas le
// JSON brut : depuis la phase 2, il porte le détail des refus de validation
// (clé inconnue, couleur invalide, libellé trop long, config trop grosse).
export function apiErrorMessage(status, body) {
    const text = String(body === null || body === undefined ? "" : body).trim();
    let message = text;
    try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.message === "string" && parsed.message.trim() !== "") {
            message = parsed.message.trim();
        }
    } catch (e) {
        // Corps non JSON (page d'erreur de la gateway) : on garde le texte brut.
    }
    return message === "" ? "HTTP " + status : "HTTP " + status + " — " + message;
}

// --- Défauts de surface ------------------------------------------------------

// parseSurfaceConfigResponse : normalise la réponse de `GET /surface-config`.
// Deux formes tolérées — la config nue par surface (`{home: {...}}`, forme
// actuelle, identique à l'endpoint public du front) et une forme enveloppée
// (`{home: {config: {...}, updatedAt, updatedBy}}`) si le back se met à exposer
// la traçabilité. Renvoie toujours les deux surfaces.
export function parseSurfaceConfigResponse(payload) {
    const source = payload && typeof payload === "object" ? payload : {};
    const out = {};
    EXPERIMENT_SURFACES.forEach(({ value: surface }) => {
        const entry = source[surface];
        if (!entry || typeof entry !== "object") {
            out[surface] = { config: {}, updatedAt: "", updatedBy: "" };
            return;
        }
        const wrapped = entry.config && typeof entry.config === "object" && !Array.isArray(entry.config);
        out[surface] = {
            config: wrapped ? entry.config : entry,
            updatedAt: (wrapped ? entry.updatedAt : entry.updatedAt) || "",
            updatedBy: (wrapped ? entry.updatedBy : entry.updatedBy) || "",
        };
    });
    return out;
}

// describeConfig : résumé humain d'une config de surface (ou de variante), une
// phrase par levier. Liste vide = aucun levier, le site rend ses défauts.
export function describeConfig(surface, config) {
    const levers = configToLevers(surface, config);
    const lines = [];

    const orderKey = surface === "product" ? "blocksOrder" : "blocks";
    const order = levers[orderKey] || [];
    if (order.length > 0) {
        lines.push(
            "Ordre des blocs : " + order.map((key, index) => index + 1 + ". " + blockLabel(surface, key)).join(", ")
        );
    }

    if (surface === "home") {
        const categories = levers.categories || [];
        if (categories.length > 0) {
            lines.push("Catégories affichées (" + categories.length + ") : " + categories.join(", "));
        }
    }

    if (surface === "product") {
        if (levers.ctaLabel) lines.push("Libellé du bouton d'achat : « " + levers.ctaLabel + " »");
        if (levers.ctaColor) lines.push("Couleur du bouton : " + levers.ctaColor);
        if (levers.highlightMerchant) lines.push("Marchand mis en avant : " + levers.highlightMerchant);
        if (levers.voucherInCta) lines.push("Code promo affiché dans le bouton d'achat.");
        if (levers.sizesFirst) lines.push("Sélecteur de taille avant la meilleure offre (mobile uniquement).");
        if (levers.bestOfferFirst) lines.push("Meilleure offre en tête, avant les photos (nouveau design, mobile uniquement).");
    }

    if (levers.variantKey) lines.push("Variante codée côté front : " + levers.variantKey);

    return lines;
}

// toApiExperiment : normalise le formulaire avant `PUT /experiment` (nombres
// en nombres, métrique dérivée de la surface, listes jamais nulles).
export function toApiExperiment(form) {
    const f = form || {};
    const surface = f.surface || "home";
    const targeting = f.targeting || {};
    const rules = f.rules || {};

    return {
        id: f.id || "",
        name: String(f.name || "").trim(),
        hypothesis: String(f.hypothesis || "").trim(),
        surface,
        status: f.status || "draft",
        variants: (Array.isArray(f.variants) ? f.variants : []).map((variant) => ({
            key: variant.key,
            name: String(variant.name || "").trim(),
            weight: Number(variant.weight) || 0,
            config: variant.config && Object.keys(variant.config).length > 0 ? variant.config : undefined,
        })),
        targeting: {
            markets: cleanList(targeting.markets),
            sources: cleanList(targeting.sources),
            devices: cleanList(targeting.devices),
            loggedIn: targeting.loggedIn || "any",
        },
        metric: { primary: METRIC_BY_SURFACE[surface] || METRIC_BY_SURFACE.home, control: CONTROL_METRIC },
        rules: {
            minDays: Number(rules.minDays) || DEFAULT_RULES.minDays,
            minExposures: Number(rules.minExposures) || 0,
            maxDays: Number(rules.maxDays) || 0,
            expectedLossThreshold: Number(rules.expectedLossThreshold) || 0,
            expectedLossThresholdRevenue: Number(rules.expectedLossThresholdRevenue) || 0,
        },
        winner: f.winner || "",
        createdBy: f.createdBy || "",
    };
}

// Suggestions de recherche (levier home `searchSuggestions`) : saisies une par
// ligne « Libellé | /lien », stockées en [{name, link}]. Lignes vides ignorées.
export function parseSuggestions(text) {
    if (Array.isArray(text)) return text;
    return String(text || "")
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== "")
        .map((line) => {
            const [name, link] = line.split("|").map((part) => (part || "").trim());
            return { name, link };
        })
        .filter((s) => s.name && s.link);
}

export function formatSuggestions(list) {
    if (!Array.isArray(list)) return "";
    return list.map((s) => `${s.name} | ${s.link}`).join("\n");
}
